import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import StudioSelect from "./StudioSelect";
import {
  getInnerCreamRange,
  getOuterCreamRange,
  getSoakRange,
  getStructuredFillingRange,
  midpointOfRange,
} from "./cakeBaseNorms.mjs";

export const CAKE_LIKE_PRODUCT_TYPE_NAMES = new Set([
  "торт",
  "бенто-торт",
  "бенто торт",
  "свадебный торт",
  "многоярусный торт",
]);

export const isCakeLikeProductType = (name) => (
  CAKE_LIKE_PRODUCT_TYPE_NAMES.has(String(name || "").trim().toLowerCase().replace(/\s+/g, " "))
);

const COMPONENTS = [
  ["sponge", "Бисквит"],
  ["inner_cream", "Крем внутрь"],
  ["filling", "Начинка"],
  ["crunchy_layer", "Хрустящий слой"],
  ["soak", "Пропитка"],
  ["outer_cream", "Крем снаружи"],
  ["decor", "Декор"],
];

const inputStyle = {
  background: "#FFFFFF",
  boxShadow: "inset 0 0 0 1px #EFE4D0",
  color: "#2A1D14",
};

const cleanQuantity = (value) => {
  const cleaned = String(value ?? "").replace(",", ".").replace(/[^\d.]/g, "");
  if (!cleaned) return null;
  const number = Number(cleaned);
  return Number.isFinite(number) && number >= 0 ? cleaned : null;
};

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? number : null;
};

export const createEmptyCakePlanning = () => ({
  grams_per_guest_min: "150",
  grams_per_guest_max: "200",
  target_weight_grams: null,
  finished_product: null,
  components: COMPONENTS.map(([component_type]) => ({ component_type, unit: "g" })),
  guidance: null,
});

const normalizePlanning = (value) => {
  const source = value || createEmptyCakePlanning();
  const byType = Object.fromEntries((source.components || []).map((row) => [row.component_type, row]));
  return {
    grams_per_guest_min: source.grams_per_guest_min ?? "150",
    grams_per_guest_max: source.grams_per_guest_max ?? "200",
    target_weight_grams: source.target_weight_grams ?? null,
    finished_product: source.finished_product || null,
    guidance: source.guidance || null,
    components: COMPONENTS.map(([component_type]) => ({
      component_type,
      unit: "g",
      ...(byType[component_type] || {}),
    })),
  };
};

const formatKg = (grams) => (grams / 1000).toLocaleString("ru-RU", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function QuantityInput({ value, onChange, testid, readOnly = false }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value ?? ""}
      readOnly={readOnly}
      onChange={(event) => onChange(cleanQuantity(event.target.value))}
      className={`w-full h-9 px-2 rounded-md text-[12px] outline-none tabular-nums ${readOnly ? "opacity-70" : ""}`}
      style={inputStyle}
      data-testid={testid}
    />
  );
}

const INNER_CREAM_OPTIONS = [
  ["sour_cream", "Сметанный крем", "light"],
  ["cream_cheese", "Крем-чиз", "light"],
  ["diplomat", "Дипломат", "light"],
  ["ganache", "Ганаш", "ganache"],
  ["buttercream", "Масляный крем", "buttercream"],
  ["whipped", "Взбитый / стабилизированный крем", "light"],
  ["other", "Другой", "manual"],
];
const FILLING_OPTIONS = [
  ["confit", "Конфи", "structured"], ["compote", "Компоте", "structured"],
  ["curd", "Курд", "structured"], ["cremeux", "Кремё", "structured"],
  ["ganache", "Ганаш", "structured"], ["caramel", "Карамель", "structured"],
  ["jam", "Джем", "structured"], ["fruit_berry", "Фруктовая / ягодная начинка", "structured"],
  ["whole_berries", "Цельные ягоды", "whole_fruit"], ["fruit", "Фрукты", "whole_fruit"],
  ["other", "Другая", "manual"],
];
const CRUNCHY_OPTIONS = [
  ["praline", "Пралине"], ["feuilletine", "Фейетин / вафельная крошка"],
  ["crumble", "Крамбл"], ["nut_crunch", "Ореховый хрустящий слой"],
  ["chocolate_crunch", "Шоколадный хрустящий слой"], ["other", "Другой"],
];

function GuidanceSelect({ label, value, onChange, options, testid, searchable = false }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.08em] flex flex-col gap-1" style={{ color: "#8B6F4B" }}>
      <span>{label}</span>
      <StudioSelect value={value || ""} onChange={(next) => onChange(next || null)}
        options={[{ value: "", label: "Не выбрано" }, ...options]}
        searchable={searchable} testid={testid} />
    </label>
  );
}

function GuidanceCount({ label, value, onChange, testid }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.08em] flex flex-col gap-1" style={{ color: "#8B6F4B" }}>
      <span>{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min="1"
        max="100"
        step="1"
        value={value ?? ""}
        onChange={(event) => {
          const number = Number(event.target.value);
          onChange(Number.isInteger(number) && number > 0 ? number : null);
        }}
        className="w-full h-9 px-2 rounded-md text-[12px] outline-none tabular-nums"
        style={inputStyle}
        data-testid={testid}
      />
    </label>
  );
}

function GuidanceHint({ range, onTakePlan, testid, sourceLabel = "Ориентир Anokina Studio" }) {
  if (!range) return null;
  return (
    <div
      className="mt-2 rounded-md px-2.5 py-2 flex flex-col min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-between gap-2 bg-[#FBF5EA]"
      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
      data-testid={testid}
    >
      <div className="text-[11px]" style={{ color: "#5B4326" }}>
        <span className="block text-[10px] uppercase tracking-[0.08em]" style={{ color: "#8B6F4B" }}>{sourceLabel}:</span>
        <span className="tabular-nums">≈ {range[0].toLocaleString("ru-RU")}–{range[1].toLocaleString("ru-RU")} г</span>
      </div>
      <button
        type="button"
        onClick={onTakePlan}
        className="h-8 px-3 rounded-xl text-[11px] shrink-0"
        style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #DCC9A8" }}
        data-testid={`${testid}-take-plan`}
      >
        Взять как план
      </button>
    </div>
  );
}

function ManualGuidanceNote({ children, testid }) {
  return <div className="mt-2 text-[10.5px] leading-snug" style={{ color: "#8B6F4B" }} data-testid={testid}>{children}</div>;
}

function ComponentGuidance({ componentType, guidance, geometry, unit, onGuidanceChange, onTakePlan, testid }) {
  if (!["inner_cream", "filling", "crunchy_layer", "soak", "outer_cream"].includes(componentType)) return null;
  const diameterCm = geometry?.diameter_cm;

  if (componentType === "inner_cream") {
    const settings = guidance?.inner_cream || {};
    const creamType = settings.cream_type || ({ light: "diplomat", buttercream: "buttercream", ganache: "ganache", manual: "other" }[settings.profile] || "");
    const range = unit === "g" ? getInnerCreamRange({
      diameterCm,
      profile: settings.profile,
      thickness: settings.thickness,
      layerCount: settings.layer_count,
    }) : null;
    const complete = settings.profile && settings.thickness && settings.layer_count;
    return (
      <div className="mb-3" data-testid={`${testid}-inner-cream-guidance`}>
        <div className="grid grid-cols-1 min-[520px]:grid-cols-3 gap-2">
          <GuidanceSelect label="Тип крема" value={creamType}
            onChange={(cream_type) => onGuidanceChange("inner_cream", {
              cream_type,
              profile: INNER_CREAM_OPTIONS.find(([value]) => value === cream_type)?.[2] || null,
              custom_name: cream_type === "other" ? settings.custom_name || null : null,
            })}
            options={INNER_CREAM_OPTIONS.map(([value, label]) => ({ value, label }))} searchable
            testid={`${testid}-inner-cream-profile`} />
          <GuidanceSelect label="Толщина прослойки" value={settings.thickness}
            onChange={(thickness) => onGuidanceChange("inner_cream", { thickness })}
            options={[{ value: "thin", label: "Тонкая" }, { value: "standard", label: "Стандартная" }, { value: "generous", label: "Щедрая" }]}
            testid={`${testid}-inner-cream-thickness`} />
          <GuidanceCount label="Кремовых прослоек" value={settings.layer_count} onChange={(layer_count) => onGuidanceChange("inner_cream", { layer_count })} testid={`${testid}-inner-cream-layers`} />
        </div>
        {creamType === "other" && <label className="mt-2 block text-[10px] uppercase tracking-[0.08em] text-[#8B6F4B]">Название крема
          <input value={settings.custom_name || ""} onChange={(event) => onGuidanceChange("inner_cream", { custom_name: event.target.value || null })}
            className="mt-1 h-9 w-full rounded-md bg-white px-2 text-[12px] normal-case tracking-normal text-[#2A1D14] outline-none shadow-[inset_0_0_0_1px_#EFE4D0]" />
        </label>}
        <GuidanceHint range={range} onTakePlan={() => onTakePlan(range)} testid={`${testid}-inner-cream-hint`} />
        {complete && !range && <ManualGuidanceNote testid={`${testid}-inner-cream-manual`}>Для выбранного профиля, толщины или размера пока укажите план вручную.</ManualGuidanceNote>}
      </div>
    );
  }

  if (componentType === "filling") {
    const settings = guidance?.filling || {};
    const fillingKind = settings.filling_kind || ({ structured: "fruit_berry", whole_fruit: "whole_berries", manual: "other" }[settings.filling_type] || "");
    const range = unit === "g" ? getStructuredFillingRange({
      diameterCm,
      fillingType: settings.filling_type,
      layerCount: settings.layer_count,
    }) : null;
    return (
      <div className="mb-3" data-testid={`${testid}-filling-guidance`}>
        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
          <GuidanceSelect label="Тип начинки" value={fillingKind}
            onChange={(filling_kind) => onGuidanceChange("filling", {
              filling_kind,
              filling_type: FILLING_OPTIONS.find(([value]) => value === filling_kind)?.[2] || null,
              custom_name: filling_kind === "other" ? settings.custom_name || null : null,
            })}
            options={FILLING_OPTIONS.map(([value, label]) => ({ value, label }))} searchable
            testid={`${testid}-filling-type`} />
          {settings.filling_type === "structured" && (
            <GuidanceCount label="Слоёв начинки" value={settings.layer_count} onChange={(layer_count) => onGuidanceChange("filling", { layer_count })} testid={`${testid}-filling-layers`} />
          )}
        </div>
        {fillingKind === "other" && <label className="mt-2 block text-[10px] uppercase tracking-[0.08em] text-[#8B6F4B]">Название начинки
          <input value={settings.custom_name || ""} onChange={(event) => onGuidanceChange("filling", { custom_name: event.target.value || null })}
            className="mt-1 h-9 w-full rounded-md bg-white px-2 text-[12px] normal-case tracking-normal text-[#2A1D14] outline-none shadow-[inset_0_0_0_1px_#EFE4D0]" />
        </label>}
        <GuidanceHint range={range} onTakePlan={() => onTakePlan(range)} testid={`${testid}-filling-hint`} />
        {settings.filling_type === "whole_fruit" && <ManualGuidanceNote testid={`${testid}-whole-fruit-manual`}>Массу цельных ягод и фруктов укажите вручную.</ManualGuidanceNote>}
        {settings.filling_type === "structured" && settings.layer_count && !range && <ManualGuidanceNote testid={`${testid}-filling-manual`}>Для выбранного размера пока укажите план вручную.</ManualGuidanceNote>}
      </div>
    );
  }

  if (componentType === "crunchy_layer") {
    const settings = guidance?.crunchy_layer || {};
    return (
      <div className="mb-3" data-testid={`${testid}-crunchy-guidance`}>
        <GuidanceSelect label="Тип хрустящего слоя" value={settings.layer_type}
          onChange={(layer_type) => onGuidanceChange("crunchy_layer", {
            layer_type, custom_name: layer_type === "other" ? settings.custom_name || null : null,
          })}
          options={CRUNCHY_OPTIONS.map(([value, label]) => ({ value, label }))} searchable
          testid={`${testid}-crunchy-type`} />
        {settings.layer_type === "other" && <label className="mt-2 block text-[10px] uppercase tracking-[0.08em] text-[#8B6F4B]">Название слоя
          <input value={settings.custom_name || ""} onChange={(event) => onGuidanceChange("crunchy_layer", { custom_name: event.target.value || null })}
            className="mt-1 h-9 w-full rounded-md bg-white px-2 text-[12px] normal-case tracking-normal text-[#2A1D14] outline-none shadow-[inset_0_0_0_1px_#EFE4D0]" />
        </label>}
      </div>
    );
  }

  if (componentType === "soak") {
    const settings = guidance?.soak || {};
    const range = unit === "g" ? getSoakRange({
      diameterCm,
      profile: settings.profile,
      cakeCount: settings.cake_count,
    }) : null;
    const complete = settings.profile && settings.cake_count;
    return (
      <div className="mb-3" data-testid={`${testid}-soak-guidance`}>
        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
          <GuidanceSelect label="Профиль пропитки" value={settings.profile} onChange={(profile) => onGuidanceChange("soak", { profile })}
            options={[{ value: "light", label: "Лёгкая" }, { value: "moderate", label: "Умеренная" }, { value: "strong", label: "Выраженная" }]}
            testid={`${testid}-soak-profile`} />
          <GuidanceCount label="Пропитываемых коржей" value={settings.cake_count} onChange={(cake_count) => onGuidanceChange("soak", { cake_count })} testid={`${testid}-soak-cakes`} />
        </div>
        <GuidanceHint range={range} onTakePlan={() => onTakePlan(range)} testid={`${testid}-soak-hint`} />
        {complete && !range && <ManualGuidanceNote testid={`${testid}-soak-manual`}>Для выбранного размера пока укажите план вручную.</ManualGuidanceNote>}
      </div>
    );
  }

  const settings = guidance?.outer_cream || {};
  const range = unit === "g" ? getOuterCreamRange({
    shape: geometry?.shape,
    diameterCm,
    heightCm: geometry?.finished_height_cm,
    ...settings,
  }) : null;
  const complete = settings.profile && settings.style;
  return (
    <div className="mb-3" data-testid={`${testid}-outer-cream-guidance`}>
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
        <GuidanceSelect label="Тип наружного крема" value={settings.profile} onChange={(profile) => onGuidanceChange("outer_cream", { profile })}
          options={[{ value: "cream_cheese_butter", label: "Крем-чиз на масле" }, { value: "buttercream", label: "Масляный крем" }, { value: "ganache", label: "Ганаш" }, { value: "whipped", label: "Взбитый / стабилизированный крем" }, { value: "manual", label: "Другой" }]}
          testid={`${testid}-outer-cream-profile`} />
        <GuidanceSelect label="Стиль покрытия" value={settings.style} onChange={(style) => onGuidanceChange("outer_cream", { style })}
          options={[{ value: "thin", label: "Тонкое" }, { value: "standard", label: "Стандартное" }, { value: "dense", label: "Плотное" }]}
          testid={`${testid}-outer-cream-style`} />
      </div>
      <GuidanceHint range={range} onTakePlan={() => onTakePlan(range)} testid={`${testid}-outer-cream-hint`} />
      {complete && !range && <ManualGuidanceNote testid={`${testid}-outer-cream-manual`}>Для подсказки нужны круглая форма, диаметр и высота до покрытия. Для неподтверждённых профилей и стилей план остаётся ручным.</ManualGuidanceNote>}
    </div>
  );
}

export default function CakePlanningHelper({ value, onChange, guestsCount, testid = "cake-planning" }) {
  const [open, setOpen] = useState(false);
  const initialTarget = numberOrNull(value?.target_weight_grams);
  const [targetUnit, setTargetUnit] = useState(initialTarget && initialTarget >= 1000 ? "kg" : "g");
  const [targetText, setTargetText] = useState(
    initialTarget === null ? "" : String(targetUnit === "kg" ? initialTarget / 1000 : initialTarget)
  );
  const planning = useMemo(() => normalizePlanning(value), [value]);

  const update = (patch) => onChange({ ...planning, ...patch });
  const updateFinished = (patch) => update({
    finished_product: { ...(planning.finished_product || {}), ...patch },
  });
  const updateGuidance = (section, patch) => update({
    guidance: {
      ...(planning.guidance || {}),
      [section]: { ...(planning.guidance?.[section] || {}), ...patch },
    },
  });
  const updateComponent = (index, patch) => {
    const components = planning.components.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    ));
    const row = components[index];
    const prepared = numberOrNull(row.prepared_quantity);
    const used = numberOrNull(row.used_quantity);
    if (prepared !== null && used !== null) {
      row.leftover_quantity = String(Math.max(prepared - used, 0));
      row.usage_ratio = prepared > 0 ? String(used / prepared) : null;
    } else {
      row.usage_ratio = null;
    }
    update({ components });
  };
  const applyGuidancePlan = (index, range) => {
    const plannedQuantity = midpointOfRange(range);
    if (plannedQuantity === null) return;
    // Deliberately update only Plan. BUILD 1A actuals and arithmetic stay untouched.
    const components = planning.components.map((row, rowIndex) => (
      rowIndex === index ? { ...row, planned_quantity: String(plannedQuantity) } : row
    ));
    update({ components });
  };

  const guests = numberOrNull(guestsCount);
  const minPerGuest = numberOrNull(planning.grams_per_guest_min);
  const maxPerGuest = numberOrNull(planning.grams_per_guest_max);
  const suggested = guests > 0 && minPerGuest > 0 && maxPerGuest > 0
    ? [guests * minPerGuest, guests * maxPerGuest]
    : null;

  const usedRows = planning.components.filter((row) => numberOrNull(row.used_quantity) !== null);
  const canForecast = usedRows.length > 0 && usedRows.every((row) => row.unit === "g");
  const forecastGrams = canForecast
    ? usedRows.reduce((sum, row) => sum + numberOrNull(row.used_quantity), 0)
    : null;
  const targetGrams = numberOrNull(planning.target_weight_grams);
  const difference = forecastGrams !== null && targetGrams !== null
    ? forecastGrams - targetGrams
    : null;

  const applyTarget = (text, unit = targetUnit) => {
    setTargetText(text);
    const number = numberOrNull(text);
    update({ target_weight_grams: number === null ? null : String(unit === "kg" ? number * 1000 : number) });
  };

  return (
    <div className="rounded-lg bg-[#FBF5EA]" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <span>
          <span className="block text-[14px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            Расчёт состава и веса
          </span>
          <span className="block text-[10.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
            По желанию · план и фактическое использование
          </span>
        </span>
        {open ? <ChevronUp size={15} color="#8B6F4B" /> : <ChevronDown size={15} color="#8B6F4B" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-4" data-testid={`${testid}-content`}>
          <div className="rounded-md bg-white p-3" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
            <div className="text-[12.5px]" style={{ color: "#2A1D14" }}>Целевой вес</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                Минимум, г/чел.
                <QuantityInput value={planning.grams_per_guest_min} onChange={(v) => update({ grams_per_guest_min: v })} testid={`${testid}-grams-min`} />
              </label>
              <label className="text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                Максимум, г/чел.
                <QuantityInput value={planning.grams_per_guest_max} onChange={(v) => update({ grams_per_guest_max: v })} testid={`${testid}-grams-max`} />
              </label>
            </div>
            <div className="mt-2 text-[11.5px]" style={{ color: "#5B4326" }} data-testid={`${testid}-guest-guide`}>
              Количество гостей: {guests > 0 ? guests : "не указано"}
              {suggested && <span className="block mt-0.5">Ориентировочный вес: {formatKg(suggested[0])}–{formatKg(suggested[1])} кг</span>}
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.12em] mb-1" style={{ color: "#8B6F4B" }}>Целевой вес готового изделия</div>
              <div className="grid grid-cols-[1fr_78px] gap-2">
                <QuantityInput value={targetText} onChange={(v) => applyTarget(v ?? "")} testid={`${testid}-target-weight`} />
                <StudioSelect
                  value={targetUnit}
                  onChange={(nextUnit) => {
                    const grams = numberOrNull(planning.target_weight_grams);
                    setTargetUnit(nextUnit);
                    const nextText = grams === null ? "" : String(nextUnit === "kg" ? grams / 1000 : grams);
                    setTargetText(nextText);
                  }}
                  options={[{ value: "g", label: "г" }, { value: "kg", label: "кг" }]}
                  testid={`${testid}-target-unit`}
                />
              </div>
            </div>
          </div>

          <div className="rounded-md bg-white p-3" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
            <div className="text-[12.5px] mb-2" style={{ color: "#2A1D14" }}>Параметры торта до наружного покрытия</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="col-span-2 text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                Форма
                <StudioSelect value={planning.finished_product?.shape || ""}
                  onChange={(shape) => updateFinished({ shape: shape || null })}
                  options={[{ value: "", label: "Не выбрано" }, { value: "round", label: "Круг" }, { value: "square", label: "Квадрат" }, { value: "rectangle", label: "Прямоугольник" }]}
                  testid={`${testid}-shape`} />
              </label>
              {planning.finished_product?.shape === "round" && (
                <label className="text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                  Диаметр до покрытия, см
                  <QuantityInput value={planning.finished_product?.diameter_cm} onChange={(v) => updateFinished({ diameter_cm: v })} testid={`${testid}-diameter`} />
                </label>
              )}
              {["square", "rectangle"].includes(planning.finished_product?.shape) && (
                <>
                  <label className="text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                    Ширина, см
                    <QuantityInput value={planning.finished_product?.width_cm} onChange={(v) => updateFinished({ width_cm: v })} testid={`${testid}-width`} />
                  </label>
                  <label className="text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                    Длина, см
                    <QuantityInput value={planning.finished_product?.length_cm} onChange={(v) => updateFinished({ length_cm: v })} testid={`${testid}-length`} />
                  </label>
                </>
              )}
              <label className="text-[10px] uppercase tracking-[0.12em]" style={{ color: "#8B6F4B" }}>
                Высота до покрытия, см
                <QuantityInput value={planning.finished_product?.finished_height_cm} onChange={(v) => updateFinished({ finished_height_cm: v })} testid={`${testid}-height`} />
              </label>
            </div>
          </div>

          <div>
            <div className="text-[12.5px] mb-2" style={{ color: "#2A1D14" }}>Компоненты</div>
            <div className="space-y-2">
              {planning.components.map((row, index) => {
                const [, label] = COMPONENTS[index];
                const prepared = numberOrNull(row.prepared_quantity);
                const used = numberOrNull(row.used_quantity);
                const derivedLeftover = prepared !== null && used !== null;
                const leftover = derivedLeftover ? Math.max(prepared - used, 0) : numberOrNull(row.leftover_quantity);
                const extra = numberOrNull(row.extra_quantity);
                return (
                  <div key={row.component_type} className="rounded-md bg-white p-3" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={`${testid}-component-${row.component_type}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-[12px]" style={{ color: "#2A1D14" }}>{label}</div>
                      <div className="w-24"><StudioSelect value={row.unit} onChange={(unit) => updateComponent(index, { unit })}
                        options={[{ value: "g", label: "г" }, { value: "ml", label: "мл" }]}
                        testid={`${testid}-${row.component_type}-unit`} /></div>
                    </div>
                    <ComponentGuidance
                      componentType={row.component_type}
                      guidance={planning.guidance}
                      geometry={planning.finished_product}
                      unit={row.unit}
                      onGuidanceChange={updateGuidance}
                      onTakePlan={(range) => applyGuidancePlan(index, range)}
                      testid={`${testid}-${row.component_type}`}
                    />
                    <div className="space-y-2">
                      {[
                        [
                          ["planned_quantity", "План"],
                          ["prepared_quantity", "Приготовлено"],
                          ["used_quantity", "Использовано"],
                        ],
                        [
                          ["leftover_quantity", "Осталось"],
                          ["extra_quantity", "Добавка"],
                        ],
                      ].map((fieldRow, fieldRowIndex) => (
                        <div
                          key={fieldRowIndex}
                          className={fieldRowIndex === 0
                            ? "grid grid-cols-1 min-[380px]:grid-cols-3 gap-2"
                            : "grid grid-cols-2 gap-2"}
                          data-testid={`${testid}-${row.component_type}-fields-row-${fieldRowIndex + 1}`}
                        >
                          {fieldRow.map(([field, fieldLabel]) => (
                            <label key={field} className="text-[10.5px] leading-tight uppercase tracking-[0.06em] flex flex-col gap-1" style={{ color: "#8B6F4B" }}>
                              <span>{fieldLabel}</span>
                              <QuantityInput
                                value={field === "leftover_quantity" && derivedLeftover ? String(leftover) : row[field]}
                                readOnly={field === "leftover_quantity" && derivedLeftover}
                                onChange={(next) => updateComponent(index, { [field]: next })}
                                testid={`${testid}-${row.component_type}-${field}`}
                              />
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                    {(numberOrNull(row.planned_quantity) !== null || used !== null || leftover > 0 || extra > 0) && (
                      <div className="mt-2 text-[10.5px]" style={{ color: "#5B4326" }}>
                        {numberOrNull(row.planned_quantity) !== null && <>План: {row.planned_quantity} {row.unit === "g" ? "г" : "мл"}</>}
                        {used !== null && <> · Использовано: {row.used_quantity} {row.unit === "g" ? "г" : "мл"}</>}
                        {leftover > 0 && <> · Осталось: {leftover} {row.unit === "g" ? "г" : "мл"}</>}
                        {extra > 0 && <> · Дополнительно: {extra} {row.unit === "g" ? "г" : "мл"}</>}
                      </div>
                    )}
                    {leftover > 0 && (
                      <label className="block mt-2 text-[10px] uppercase tracking-[0.1em]" style={{ color: "#8B6F4B" }}>
                        Что сделать с остатком?
                        <div className="mt-1"><StudioSelect value={row.leftover_action || ""}
                          onChange={(leftover_action) => updateComponent(index, { leftover_action: leftover_action || null })}
                          options={[{ value: "", label: "Не выбрано" }, { value: "use_later", label: "Использую позже" }, { value: "write_off", label: "Списать на этот заказ" }]}
                          testid={`${testid}-${row.component_type}-leftover-action`} /></div>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-md bg-white p-3" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={`${testid}-forecast`}>
            {forecastGrams !== null ? (
              <>
                <div className="text-[13px]" style={{ color: "#2A1D14" }}>Прогноз итогового веса: ≈ {formatKg(forecastGrams)} кг</div>
                <div className="mt-1 text-[11px]" style={{ color: "#8B6F4B" }}>
                  Целевой вес: {targetGrams !== null ? `${formatKg(targetGrams)} кг` : "не указан"}
                  {difference !== null && <> · Разница: {difference >= 0 ? "+" : "−"}{Math.abs(difference).toLocaleString("ru-RU")} г</>}
                </div>
              </>
            ) : usedRows.length > 0 ? (
              <div className="text-[11px]" style={{ color: "#8B6F4B" }}>Для общего прогноза веса укажите использованные компоненты в граммах.</div>
            ) : (
              <div className="text-[11px]" style={{ color: "#8B6F4B" }}>Прогноз появится после заполнения поля «Использовано».</div>
            )}
          </div>
          <div className="text-[10.5px] leading-snug" style={{ color: "#8B6F4B" }}>
            Данные помогают сравнить план и фактическое использование. Они не меняют себестоимость, заказ или фактический вес автоматически.
          </div>
        </div>
      )}
    </div>
  );
}
